import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import config from '../config';
import * as stripeService from '../utils/stripeService';
import './SubscriptionPage.css';

const SubscriptionPage = () => {
  const { user, hasActiveSubscription, loading, subscriptionLoading } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleSubscribe = async () => {
    if (!user?.id || !user?.email) {
      setError('User information is missing. Please log in again.');
      return;
    }

    try {
      setCheckoutLoading(true);
      setError(null);
      
      const checkoutUrl = await stripeService.createCheckoutSession(
        user.id,
        user.email
      );
      
      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to create checkout session. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user?.id) {
      setError('User information is missing. Please log in again.');
      return;
    }

    try {
      setCheckoutLoading(true);
      setError(null);
      
      // Store email in localStorage for future use
      if (user.email) {
        localStorage.setItem('userEmail', user.email);
      }
      
      const portalUrl = await stripeService.createPortalSession(user.id, user.email);
      
      // Redirect to Stripe customer portal
      window.location.href = portalUrl;
    } catch (err) {
      console.error('Error creating portal session:', err);
      setError(err.message || 'Failed to create portal session. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Show loading state
  if (loading || subscriptionLoading) {
    return (
      <div className="subscription-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading subscription information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="container">
        <div className="subscription-header">
          <h1 className="page-title">Subscription</h1>
          <p className="page-subtitle">
            {hasActiveSubscription 
              ? "Manage your subscription settings and billing information" 
              : "Upgrade to " + config.stripe.productName + " for unlimited access to all features"}
          </p>
        </div>

        {error && (
          <Alert 
            type="error" 
            title="Error" 
            message={error} 
            className="mb-md"
            dismissible
            onDismiss={() => setError(null)}
          />
        )}

        <div className="subscription-content">
          {hasActiveSubscription ? (
            <Card className="subscription-card active-subscription">
              <div className="subscription-card-header">
                <h2>Active Subscription</h2>
                <div className="subscription-badge">Active</div>
              </div>
              
              <div className="subscription-card-content">
                <p>
                  You currently have an active subscription to {config.stripe.productName}.
                  You have full access to all features.
                </p>
                
                <div className="subscription-features">
                  <h3>Your Benefits</h3>
                  <ul>
                    {config.subscription.features.map((feature, index) => (
                      <li key={index} className="feature-active">
                        <span className="feature-check">✓</span> {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="subscription-card-footer">
                <Button 
                  onClick={handleManageSubscription}
                  loading={checkoutLoading}
                  disabled={checkoutLoading}
                >
                  Manage Subscription
                </Button>
                
                <Link to="/dashboard">
                  <Button variant="secondary">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            </Card>
          ) : (
            <Card className="subscription-card">
              <div className="subscription-card-header">
                <h2>{config.stripe.productName}</h2>
                <div className="subscription-price">
                  ${config.stripe.amount}<span>/month</span>
                </div>
              </div>
              
              <div className="subscription-card-content">
                <p>
                  Upgrade to {config.stripe.productName} for unlimited access to all features.
                  {config.stripe.productDescription}
                </p>
                
                <div className="subscription-features">
                  <h3>Subscription Benefits</h3>
                  <ul>
                    {config.subscription.features.map((feature, index) => (
                      <li key={index}>
                        <span className="feature-check">✓</span> {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="subscription-card-footer">
                <Button 
                  onClick={handleSubscribe}
                  loading={checkoutLoading}
                  disabled={checkoutLoading}
                >
                  Subscribe Now
                </Button>
                
                <Link to="/dashboard">
                  <Button variant="secondary">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
              
              <div className="subscription-secure-payment">
                <Alert 
                  type="info"
                  title="Secure Payment"
                  message="All payments are processed securely through Stripe."
                />
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
